import { Close } from "@mui/icons-material";
import {
  Alert,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useState } from "react";
import { useUploadFileMutation } from "src/api/services/bucketApi";
import { AlertProps, FileType } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import ImageUploader from "src/components/ImageUploader";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { convertToBlob } from "src/utils/convertToBlob";
import { desktopSx } from "src/utils/styles/modalStyles";

const mapFilesToFolder: Record<string, string[]> = {
  insurance: ["front-of-card", "back-of-card"],
  identification: ["front-of-card", "self-portrait"],
};

type Props = {
  open: boolean;
  onClose: () => void;
  bucket: string;
  folder: string;
  patientProfileId?: string;
};

const DocumentUploadModal = ({
  onClose,
  open,
  bucket,
  folder,
  patientProfileId,
}: Props) => {
  const [uploadFile] = useUploadFileMutation();
  const [file, setFile] = useState<FileType | null>(null);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const errorLog = useSentryLogger("error");
  const [alert, setAlert] = useState<AlertProps>({
    display: false,
    message: "",
    severity: "success",
  });

  const items = mapFilesToFolder[folder];

  const handleClose = useCallback(() => {
    setAlert({ display: false, message: "", severity: "success" });
    setFile(null);
    onClose();
  }, [onClose]);

  const handleImage = useCallback((file: FileType | null) => {
    setFile(file);
  }, []);

  const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSelectedItem(e.target.value);
  }, []);

  const handleUploadFile = useCallback(async () => {
    if (file == null) return;
    setLoading(true);
    try {
      const blob = await convertToBlob(file.fileToUpload);
      let path: string;

      if (patientProfileId != null) {
        path = `patient-${patientProfileId}/${folder}`;
      } else {
        path = folder;
      }

      await uploadFile({
        file: blob,
        bucket,
        path,
        name: selectedItem,
      });

      setAlert({
        display: true,
        message: `Successfully uploaded ${selectedItem} to ${folder}`,
        severity: "success",
      });
    } catch (error) {
      setAlert({
        display: true,
        message: `Successfully uploaded ${selectedItem} to ${folder}`,
        severity: "success",
      });
      errorLog(`Unable to upload file ${selectedItem}`, error);
    } finally {
      setLoading(false);
    }
  }, [
    bucket,
    errorLog,
    file,
    folder,
    patientProfileId,
    selectedItem,
    uploadFile,
  ]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={{ maxWidth: 500, ...desktopSx }} gap={3}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography variant="h5" fontWeight={600}>
            Upload documents
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
        <TextField
          select
          onChange={handleOnChange}
          value={selectedItem}
          label="File"
        >
          {items.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
        <ImageUploader
          file={file}
          folder={folder}
          name={selectedItem}
          setFilePath={handleImage}
          subtitle={null}
          title="Front of card"
        />
        <LoadingButton loading={loading} onClick={handleUploadFile}>
          Upload
        </LoadingButton>
        {alert.display ? (
          <Alert severity={alert.severity}>
            <Typography>{alert.message}</Typography>
          </Alert>
        ) : null}
      </Stack>
    </Modal>
  );
};

export default DocumentUploadModal;
